import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { useGetManagedHatimTemplatesQuery } from "../../api/hatimManagementApi";
import HatimListPanel from "./components/HatimListPanel";
import HatimDetailPanel from "./components/HatimDetailPanel";
import CreateHatimTemplateModal from "./components/CreateHatimTemplateModal";

/**
 * Hatim Yönetimi: grup yönetimiyle aynı kalıp — solda yönetilen hatimler,
 * sağda seçili hatimin sekmeli detayı. `/hatim-yonetim/:templateId` adresi
 * o hatimi seçili açar; satır seçince adres güncellenir.
 */
export default function HatimTemplateListPage() {
  const { t } = useScriptMode();
  const toast = useToast();
  const navigate = useNavigate();
  const { templateId: routeId } = useParams();

  const { data, isLoading, refetch } = useGetManagedHatimTemplatesQuery();
  const templates = useMemo(() => data?.data?.templates || [], [data]);

  const [selectedId, setSelectedId] = useState(routeId || null);
  const [createOpen, setCreateOpen] = useState(false);

  // Adres değişirse (grup sekmesinden gelen bağlantı) seçimi ona bağla.
  useEffect(() => {
    if (routeId) setSelectedId(String(routeId));
  }, [routeId]);

  // İlk hatimi seç; seçili hatim listeden düşerse (silme) seçimi tazele.
  useEffect(() => {
    if (isLoading) return;
    if (templates.length === 0) {
      setSelectedId(null);
      return;
    }
    const stillThere = templates.some((tpl) => String(tpl._id) === String(selectedId));
    if (!stillThere) setSelectedId(String(templates[0]._id));
  }, [templates, selectedId, isLoading]);

  const handleSelect = (id) => {
    setSelectedId(id);
    navigate(`/uygulama/hatim-yonetim/${id}`, { replace: true });
  };

  const selected = templates.find((tpl) => String(tpl._id) === String(selectedId)) || null;

  return (
    <div className="w-full flex flex-col gap-6 lg:h-full lg:min-h-0">
      <div className="tk-stagger grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-6 items-start lg:items-stretch lg:flex-1 lg:min-h-0">
        <HatimListPanel
          templates={templates}
          selectedId={selectedId}
          loading={isLoading}
          onSelect={handleSelect}
          onCreate={() => setCreateOpen(true)}
        />
        <HatimDetailPanel
          template={selected}
          onDeleted={() => {
            setSelectedId(null);
            navigate("/uygulama/hatim-yonetim", { replace: true });
            refetch();
          }}
        />
      </div>

      <CreateHatimTemplateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          toast.success(t("hatim_mgmt_create_success"));
          if (id) handleSelect(String(id));
        }}
      />
    </div>
  );
}
